import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  PinMemberInput,
  TeamInferenceResult,
  UpdateInferredTeamInput,
} from "@aura/shared";
import { api, type ApiResponse } from "~/lib/api";
import { teamsKeys } from "./queries";

// Admin overrides. Each one refreshes every teams query on success.

function useInvalidateTeams() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: teamsKeys.all });
}

/** POST /api/teams/infer */
export function useInferTeams() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: async () =>
      (await api.post<ApiResponse<TeamInferenceResult>>("/teams/infer")).data,
    onSuccess: invalidate,
  });
}

/** PUT /api/teams/:teamId/members/:developerId. `teamId` is the team they land on. */
export function usePinMember() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({
      teamId,
      developerId,
      input,
    }: {
      teamId: string;
      developerId: string;
      input: PinMemberInput;
    }) =>
      api.put<unknown>(
        `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(developerId)}`,
        input,
      ),
    onSuccess: invalidate,
  });
}

/** PATCH /api/teams/:teamId to rename or hide. */
export function useUpdateTeam() {
  const invalidate = useInvalidateTeams();
  return useMutation({
    mutationFn: ({ teamId, input }: { teamId: string; input: UpdateInferredTeamInput }) =>
      api.patch<unknown>(`/teams/${encodeURIComponent(teamId)}`, input),
    onSuccess: invalidate,
  });
}
